import { IRynReplaySessionSummary } from './ryn-replay-session-source';
import { IRynReplayUserDisplay } from './ryn-replay-user-resolver';

/**
 * Who the session belongs to, short enough for a grid cell.
 *
 * Prefer a host-resolved full name, then the row's read-time userName/email,
 * then the first Guid segment so two rows for the same unknown person still
 * match. Says "Not signed in" instead of leaving the cell empty: an empty
 * cell reads as data we failed to load, when the usual reason is a genuinely
 * anonymous session.
 */
export const replaySessionUserLabel = (
  row: IRynReplaySessionSummary,
  names: ReadonlyMap<string, IRynReplayUserDisplay>
): string => {
  if (!row.securityUserId) {
    return 'Not signed in';
  }
  return (
    names.get(row.securityUserId)?.name ??
    row.userName?.trim() ??
    row.email?.trim() ??
    'Signed-in user'
  );
};

/** Full Software Security user id for copy-on-click; undefined when anonymous. */
export const replaySessionUserCopyId = (
  row: IRynReplaySessionSummary
): string | undefined => {
  const id = row.securityUserId?.trim();
  return id || undefined;
};

/** Email under the name when it is not already the cell's primary label. */
export const replaySessionUserEmail = (
  row: IRynReplaySessionSummary,
  names: ReadonlyMap<string, IRynReplayUserDisplay>
): string | undefined => {
  if (!row.securityUserId) {
    return undefined;
  }
  const email =
    names.get(row.securityUserId)?.email?.trim() ||
    row.email?.trim() ||
    undefined;
  if (!email || email === replaySessionUserLabel(row, names)) {
    return undefined;
  }
  return email;
};

/**
 * Tooltip for the user cell: preferred name/email, then the Guid caveat.
 *
 * The caveat is here rather than only in a comment because this is the one
 * place a person sees the value and might act on it. The client asserted this
 * id and the server could not verify it, so it is good for finding a recording
 * and not good for concluding who was at the keyboard.
 */
export const replaySessionUserTitle = (
  row: IRynReplaySessionSummary,
  names: ReadonlyMap<string, IRynReplayUserDisplay>
): string => {
  if (!row.securityUserId) {
    return 'No user was signed in during the recorded part of this session.';
  }
  const parts: string[] = [];
  const display = names.get(row.securityUserId);
  if (display?.name?.trim()) {
    parts.push(display.name.trim());
  } else if (row.userName?.trim()) {
    parts.push(row.userName.trim());
  }
  const email = display?.email?.trim() || row.email?.trim();
  if (email && !parts.includes(email)) {
    parts.push(email);
  }
  parts.push(`Software Security user ${row.securityUserId}`);
  parts.push(
    'Reported by the app, not verified by the server, so use it to find sessions rather than to attribute actions.'
  );
  return parts.join('. ');
};
